import { useEffect, useState } from "react";
import { api, itemImage } from "@/lib/api";
import { LookImage } from "@/components/LookImage";
import { HeartButton, AddToDaylist, SwapPieceDialog, OccasionBadge } from "@/components/Lookbook";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Sparkles, Umbrella, Share2, Loader2, Replace, Plus, Lock, LockOpen, Shuffle, Download } from "lucide-react";
import { toast } from "sonner";

/** Share a rendered look to the social feed. */
const ShareLook = ({ outfit }) => {
  const [open, setOpen] = useState(false);
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const share = async () => {
    setBusy(true);
    try {
      const who = localStorage.getItem("wardrobe.lookWho") || "model";
      await api.createPost({ outfit_id: outfit.id, who, view: "front", caption });
      toast.success("Shared to your feed");
      setOpen(false); setCaption("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not share");
    } finally { setBusy(false); }
  };
  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} className="rounded-full" data-testid="outfit-share-button"><Share2 className="h-4 w-4 mr-1.5" /> Share</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="share-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Share this look</DialogTitle><DialogDescription>Posts the rendered photo and the pieces to your profile and the feed.</DialogDescription></DialogHeader>
          <Input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Caption (optional)" data-testid="share-caption-input" />
          <Button onClick={share} disabled={busy} className="rounded-full" data-testid="share-submit-button">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Post</Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

/**
 * Outfit = hero photo of the look worn on a model / you + the pieces alongside.
 * Click any piece to swap it. Heart it. Drop it into a Daylist.
 * `compact` (week view) renders on demand to avoid 7 generations at once.
 */
export const OutfitCard = ({ outfit: initial, compact = false, onChange, onShuffle, shuffling = false }) => {
  const [outfit, setOutfit] = useState(initial);
  const [swap, setSwap] = useState(null); // {piece} | {piece:null} for add
  const [renderKey, setRenderKey] = useState(0);
  const [locked, setLocked] = useState([]);
  useEffect(() => { setOutfit(initial); setLocked((l) => l.filter((id) => (initial?.items || []).some((i) => i.id === id))); }, [initial]);
  const items = outfit?.items || [];
  const toggleLock = (id) => setLocked((l) => (l.includes(id) ? l.filter((x) => x !== id) : [...l, id]));
  const saveLook = async () => {
    try {
      const who = localStorage.getItem("wardrobe.lookWho") || "model";
      const res = await fetch(api.outfitLookUrl(outfit.id, "front", who), { credentials: "include" });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `threadlist-look-${outfit.date || "today"}.png`; a.click(); URL.revokeObjectURL(a.href);
    } catch { toast.error("Render the look first, then save it"); }
  };

  const onSwapped = (o) => { setOutfit(o); setRenderKey((k) => k + 1); onChange?.(o); };

  if (items.length === 0 && !swap) {
    return (
      <div data-testid="outfit-card" className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
        No matching items found.
      </div>
    );
  }

  if (compact) {
    return (
      <div data-testid="outfit-card" className="grid grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] gap-3">
        <div className="relative">
          <LookImage key={renderKey} outfitId={outfit.id} auto={false} className="aspect-[3/4]" testId={`outfit-look-${outfit.id}`} />
          <div className="absolute bottom-2 right-2"><HeartButton outfit={outfit} onChange={(f) => onChange?.({ ...outfit, favorite: f })} /></div>
        </div>
        <div className="grid grid-cols-2 gap-2 content-start">
          {items.map((it) => (
            <button key={it.id} onClick={() => setSwap({ piece: it })} className="group relative rounded-lg overflow-hidden bg-secondary ring-1 ring-black/5 aspect-[3/4]" title={`Swap ${it.name}`} data-testid={`outfit-item-${it.id}`}>
              <img src={itemImage(it)} alt={it.name} loading="lazy" className="h-full w-full object-cover" />
              <span className="absolute inset-0 hidden group-hover:flex items-center justify-center bg-background/60 text-[10px] font-medium"><Replace className="h-3.5 w-3.5 mr-1" /> Swap</span>
            </button>
          ))}
        </div>
        {swap && <SwapPieceDialog outfit={outfit} piece={swap.piece} open onOpenChange={(o) => !o && setSwap(null)} onSwapped={onSwapped} />}
      </div>
    );
  }

  return (
    <div data-testid="outfit-card">
      {(outfit.name || outfit.occasion || outfit.edited) && (
        <div className="flex items-center gap-2 mb-3">
          {outfit.name && <h3 className="font-display text-xl" data-testid="outfit-name">{outfit.name}</h3>}
          <OccasionBadge occasion={outfit.occasion} />
          {outfit.edited && <span className="text-[10px] rounded-full bg-secondary px-2 py-0.5 text-muted-foreground">edited by you</span>}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)] gap-4">
        <div className="relative">
          <LookImage key={renderKey} outfitId={outfit.id} className="aspect-[3/4]" />
          <div className="absolute top-2 right-2"><HeartButton outfit={outfit} onChange={(f) => onChange?.({ ...outfit, favorite: f })} /></div>
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="eyebrow">The pieces · tap to swap{onShuffle ? " · lock to keep" : ""}</p>
            <button onClick={() => setSwap({ piece: null })} className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground" data-testid="outfit-add-piece"><Plus className="h-3.5 w-3.5" /> Add</button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {items.map((it) => {
              const isLocked = locked.includes(it.id);
              return (
                <div key={it.id} className={`group relative text-left rounded-xl overflow-hidden bg-gray-50 border transition-colors ${isLocked ? "border-foreground" : "border-border hover:border-foreground/40"}`} data-testid={`outfit-item-${it.id}`}>
                  <button onClick={() => setSwap({ piece: it })} className="block w-full text-left" title={`Swap ${it.name}`}>
                    <div className="relative aspect-[3/4]">
                      <img src={itemImage(it)} alt={it.name} loading="lazy" className="h-full w-full object-cover mix-blend-multiply" />
                      <span className="absolute inset-x-0 bottom-0 hidden group-hover:flex items-center justify-center gap-1 py-1.5 text-[10px] font-medium bg-background/85 backdrop-blur"><Replace className="h-3 w-3" /> Swap this</span>
                    </div>
                    <div className="p-2 pr-8 text-[11px] truncate font-medium">{it.name || it.subtype}</div>
                  </button>
                  {onShuffle && (
                    <button onClick={() => toggleLock(it.id)} className={`absolute top-1.5 right-1.5 h-7 w-7 rounded-full inline-flex items-center justify-center transition-colors ${isLocked ? "bg-foreground text-background" : "bg-background/90 text-muted-foreground hover:text-foreground"}`} aria-pressed={isLocked} aria-label={isLocked ? "Unlock piece" : "Lock piece"} data-testid={`outfit-lock-${it.id}`}>
                      {isLocked ? <Lock className="h-3.5 w-3.5" /> : <LockOpen className="h-3.5 w-3.5" />}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          {onShuffle && (
            <Button variant="outline" size="sm" onClick={() => onShuffle(locked)} disabled={shuffling} className="rounded-full mt-3 w-full" data-testid="outfit-shuffle-button">
              {shuffling ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Shuffle className="h-4 w-4 mr-1.5" />}{locked.length ? `Shuffle the rest (keeping ${locked.length})` : "Shuffle"}
            </Button>
          )}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={saveLook} className="rounded-full" data-testid="outfit-save-look-button"><Download className="h-4 w-4 mr-1.5" /> Save</Button>
        <AddToDaylist outfit={outfit} />
        <ShareLook outfit={outfit} />
      </div>
      {outfit?.reasoning && (
        <div className="mt-2 flex gap-2 text-sm">
          <Sparkles className="h-4 w-4 mt-0.5 shrink-0" />
          <p className="text-muted-foreground" data-testid="outfit-reasoning">{outfit.reasoning}</p>
        </div>
      )}
      {outfit?.rain_backup && (
        <div className="mt-2 flex gap-2 text-sm">
          <Umbrella className="h-4 w-4 mt-0.5 shrink-0 text-info" />
          <p className="text-muted-foreground">{outfit.rain_backup}</p>
        </div>
      )}
      {swap && <SwapPieceDialog outfit={outfit} piece={swap.piece} open onOpenChange={(o) => !o && setSwap(null)} onSwapped={onSwapped} />}
    </div>
  );
};
